import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Picture from '@deporvillage/picture-component';
import Tooltip, { TooltipPosition } from '@deporvillage/tooltip-component';
import Checkbox from '@deporvillage/checkbox-component';
import Sidebar, { SidebarPosition } from '@deporvillage/sidebar-component';
import { Config, I18n, I18nContext, StockLabel } from '@deporvillage/utils-library-component';
import Usizy, { UsizyClick } from './Usizy.js';

import styles from './Variant.module.scss';

const SizeRecommendationI18n = {
    "-2": "product_page.sizing.two_sizes_smaller",
    "-1": "product_page.sizing.one_size_smaller",
    "-0.5": "product_page.sizing.half_size_smaller",
    "0.5": "product_page.sizing.half_size_larger",
    "1": "product_page.sizing.one_size_larger",
    "2": "product_page.sizing.two_sizes_larger",
};

const Variant = ({ placeholder, options, recommendation, fit, sizeGuide, price, sku, change, onClick }) => {

    const { isMobile } = React.useContext(Config);
    const { translate } = React.useContext(I18nContext);

    const [toggleSidebar, setToggleSidebar] = React.useState(false);
    const [toggleSidebarPlaceholder, setToggleSidebarPlaceholder] = React.useState(placeholder || '');
    const [ops, setOptions] = React.useState(options.map(i => Object.assign(i, {
        disabled: i.disabled || (i.stockLabel === StockLabel.NoStock && !i.replenishment),
        checked: i.checked || false,
    })));

    const showUsizy = options.filter(({ stockLabel, replenishment }) => stockLabel !== StockLabel.NoStock || replenishment).length;

    const handleOptionChange = (match, checked) => {
        const nextOps = ops.map((o, idx) => {
            o.checked = (match == idx) ? checked : false;
            return o;
        });
        setOptions(nextOps);
        setToggleSidebar(false);
        setToggleSidebarPlaceholder(ops[match].name);
        change && change(nextOps[match]);
    };

    /* istanbul ignore next */
    const handleUsizyClick = (event) => onClick && onClick(event);
    const handleToggleSidebar = () => setToggleSidebar(!toggleSidebar);

    const renderVariantsContainer = () => (
        <div className={styles['product-variant-container']}>
            {ops.map((o, idx) =>
                <div className={styles['product-variant-container_item']}
                    key={o.name}>
                    <Checkbox id={`option-selector-${idx}`}
                        checked={o.checked}
                        change={({ checked }) => handleOptionChange(idx, checked)}
                        disabled={o.disabled} />
                    <label htmlFor={`option-selector-${idx}`}>{o.name}</label>
                </div>
            )}
        </div>
    );

    const renderFittingDetails = ({ image, translation}) => (
        <>
            <Picture
                src={image}
                name={translate(`${translation}.description`)}
                height={50}
                width={50}
                className={styles['product-fit-img']}
                lazy
                resize
            />
            <div className={styles['product-fit-description']}>
                <strong><I18n>{`${translation}.title`}</I18n></strong>
                <span><I18n>{`${translation}.description`}</I18n></span>
            </div>
        </>
    );

    return (
        <div className={styles['product-variant-component']} >
            <p className={styles['product-action-title']}><I18n>product_page.title.sizing</I18n></p>
            {!fit ? null :
                <div className={`${styles['product-fit']} ${recommendation ? '' : styles['product-fit-recommendation']}`}>

                        <div className={styles['product-fit-wrapper']}>
                            {renderFittingDetails(fit)}
                        </div>

                        <div className={styles['product-fit-wrapper']}>
                            <I18n>{`${fit.translation}.title`}</I18n> <i className={`${styles['icon']} icon icon-fit-${fit.type}`} />
                            <div className={styles['tooltip-wrapper']}>
                                <Tooltip
                                    position={TooltipPosition.Right}
                                    content={
                                        <div className={styles['tooltip-content']}>
                                            {renderFittingDetails(fit)}
                                        </div>
                                    }
                                />
                            </div>
                        </div>

                </div>
            }

            {(recommendation || showUsizy) && sizeGuide ? (
                <div className={`${styles['product-size-recommendation']} ${recommendation ? '' : styles['product-size-recommendation-width']}`}>

                    {recommendation ? (
                        <Fragment>
                            <span dangerouslySetInnerHTML={{
                                __html: translate(SizeRecommendationI18n[recommendation])
                            }} />
                        </Fragment>
                    ) : null}

                    {showUsizy ? (
                        <Usizy price={price.pvp}
                            url={sizeGuide}
                            sku={sku}
                            onClick={handleUsizyClick}
                        />
                    ) : null}
                </div>
            ) : null}

            <div className={styles['product-action-size-select']}>
                <button data-testid="toggle-sidebar" onClick={handleToggleSidebar} className={styles['btn-select']}>{toggleSidebarPlaceholder}</button>
                {isMobile ?
                    <Sidebar open={toggleSidebar} onClose={handleToggleSidebar} header={placeholder} position={SidebarPosition.Right}>
                        {renderVariantsContainer()}
                    </Sidebar>
                    :
                    <div className={styles['sidebar-wrapper']}>
                        {renderVariantsContainer()}
                    </div>
                }
            </div>
        </div>
    );
};

Variant.propTypes = {
    change: PropTypes.func,
    placeholder: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
    ]),
    fit: PropTypes.shape({
        image: PropTypes.string,
        translation: PropTypes.string,
        type: PropTypes.string,
    }),
    sizeGuide: PropTypes.string,
    recommendation: PropTypes.number,
    price: PropTypes.shape({
        pvp: PropTypes.number,
    }),
    sku: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    })),
    onClick: PropTypes.func,
};

export default Variant;
export { UsizyClick as VariantUsizyClick };
